import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Package, ShoppingBag, Clock, CheckCircle, Truck, XCircle, Eye, EyeOff, ChevronLeft, ChevronRight, AlertCircle, RefreshCw, MapPin, MessageSquare, Send, Box, PackageCheck, Shield, ShieldAlert, ShieldCheck, Star, ThumbsUp, AlertTriangle } from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'

const DELIVERY_STATUSES = [
  { value: 'pending', label: 'Pending', icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100', ring: 'ring-yellow-500' },
  { value: 'preparing', label: 'Preparing', icon: Box, color: 'text-orange-600', bg: 'bg-orange-100', ring: 'ring-orange-500' },
  { value: 'shipped', label: 'Shipped', icon: Truck, color: 'text-blue-600', bg: 'bg-blue-100', ring: 'ring-blue-500' },
  { value: 'in_transit', label: 'In Transit', icon: Truck, color: 'text-indigo-600', bg: 'bg-indigo-100', ring: 'ring-indigo-500' },
  { value: 'delivered', label: 'Delivered', icon: PackageCheck, color: 'text-green-600', bg: 'bg-green-100', ring: 'ring-green-500' }
]

const KataleOrders = () => {
  const { customer } = useCustomerAuth()
  const [activeTab, setActiveTab] = useState('purchases')
  const [loading, setLoading] = useState(true)
  const [orders, setOrders] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [error, setError] = useState('')
  const [expandedOrder, setExpandedOrder] = useState(null)
  const [updatingDelivery, setUpdatingDelivery] = useState(null)
  const [deliveryForm, setDeliveryForm] = useState({ status: '', notes: '' })
  const [confirmingReceipt, setConfirmingReceipt] = useState(null)
  const [showDisputeModal, setShowDisputeModal] = useState(false)
  const [disputeOrder, setDisputeOrder] = useState(null)
  const [disputeForm, setDisputeForm] = useState({ reason: '', description: '' })
  const [submittingDispute, setSubmittingDispute] = useState(false)
  const ordersPerPage = 10

  useEffect(() => {
    if (customer) {
      fetchOrders()
    }
  }, [activeTab, customer])

  const getMgCode = (tenantDb) => {
    if (!tenantDb) return null
    const prefix = 'mgfina6_tenant_'
    if (tenantDb.startsWith(prefix)) {
      return tenantDb.substring(prefix.length)
    }
    const parts = tenantDb.split('_')
    return parts.length > 1 ? parts[1] : tenantDb
  }

  const fetchOrders = async () => {
    if (!customer) return
    
    const userMemNo = customer.memberNo
    const userMgCode = getMgCode(customer.tenantDb)
    
    if (!userMemNo || !userMgCode) return
    
    setLoading(true)
    setError('')
    
    try {
      const response = await apiService.getKataleOrders(userMemNo, userMgCode, activeTab)
      
      if (response.status === 'SUCCESS' && response.data) {
        setOrders(response.data || [])
      } else {
        setError(response.message || 'Failed to load orders')
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err)
      setError('Failed to load orders. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateDeliveryStatus = async (orderId) => {
    if (!deliveryForm.status) return
    
    const userMemNo = customer.memberNo
    const userMgCode = getMgCode(customer.tenantDb)
    
    setUpdatingDelivery(orderId)
    
    try {
      const response = await apiService.updateKataleDeliveryStatus(
        orderId,
        deliveryForm.status,
        deliveryForm.notes,
        userMemNo,
        userMgCode
      )
      
      if (response.status === 'SUCCESS') {
        setOrders(orders.map(order => 
          order.id === orderId 
            ? { 
                ...order, 
                delivery_status: deliveryForm.status,
                delivery_notes: deliveryForm.notes || order.delivery_notes,
                delivery_updated_at: new Date().toISOString(),
                status: response.data?.status || order.status
              } 
            : order
        ))
        setDeliveryForm({ status: '', notes: '' })
      } else {
        alert(response.message || 'Failed to update delivery status')
      }
    } catch (err) {
      console.error('Failed to update delivery:', err)
      alert('Failed to update delivery status')
    } finally {
      setUpdatingDelivery(null)
    }
  }

  const toggleDetails = (orderId) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null)
      setDeliveryForm({ status: '', notes: '' })
    } else {
      setExpandedOrder(orderId)
      const order = orders.find(o => o.id === orderId)
      if (order) {
        setDeliveryForm({ status: order.delivery_status || 'pending', notes: '' })
      }
    }
  }

  // Buyer confirms delivery receipt → releases escrow funds to seller
  const handleConfirmDeliveryReceipt = async (orderId, confirmationType) => {
    const userMemNo = customer.memberNo
    const userMgCode = getMgCode(customer.tenantDb)
    setConfirmingReceipt(orderId)

    try {
      const response = await apiService.confirmKataleDeliveryReceipt(
        orderId, userMemNo, userMgCode, confirmationType
      )
      if (response.status === 'SUCCESS') {
        setOrders(orders.map(order =>
          order.id === orderId
            ? {
                ...order,
                escrow_status: confirmationType === 'accepted' ? 'released' : 'disputed',
                buyer_confirmed_receipt: confirmationType === 'accepted',
                status: confirmationType === 'accepted' ? 'completed' : order.status
              }
            : order
        ))
        if (confirmationType === 'accepted') {
          alert('✅ Delivery confirmed! Escrow funds will be released to the seller.')
        }
      } else {
        alert(response.message || 'Failed to confirm delivery')
      }
    } catch (err) {
      console.error('Confirm receipt error:', err)
      alert('Failed to confirm delivery receipt')
    } finally {
      setConfirmingReceipt(null)
    }
  }

  // Buyer raises a dispute
  const handleRaiseDispute = async () => {
    if (!disputeForm.reason) { alert('Please select a reason for the dispute'); return }
    if (!disputeForm.description.trim()) { alert('Please describe the issue'); return }

    const userMemNo = customer.memberNo
    const userMgCode = getMgCode(customer.tenantDb)
    setSubmittingDispute(true)

    try {
      const response = await apiService.raiseKataleDispute(
        disputeOrder.id, userMemNo, userMgCode,
        disputeForm.reason, disputeForm.description
      )
      if (response.status === 'SUCCESS') {
        setShowDisputeModal(false)
        setDisputeOrder(null)
        setDisputeForm({ reason: '', description: '' })
        fetchOrders()
        alert('Dispute raised successfully. UFCC will review and mediate.')
      } else {
        alert(response.message || 'Failed to raise dispute')
      }
    } catch (err) {
      alert('Failed to raise dispute: ' + err.message)
    } finally {
      setSubmittingDispute(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-UG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('en-UG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending':
        return { icon: Clock, color: 'text-yellow-600 bg-yellow-100', label: 'Pending' }
      case 'processing':
        return { icon: Clock, color: 'text-orange-600 bg-orange-100', label: 'Processing' }
      case 'shipped':
        return { icon: Truck, color: 'text-blue-600 bg-blue-100', label: 'Shipped' }
      case 'delivered':
      case 'completed':
        return { icon: CheckCircle, color: 'text-green-600 bg-green-100', label: 'Delivered' }
      case 'cancelled':
        return { icon: XCircle, color: 'text-red-600 bg-red-100', label: 'Cancelled' }
      default:
        return { icon: Clock, color: 'text-gray-600 bg-gray-100', label: status }
    }
  }

  const getDeliveryStatusIndex = (status) => {
    return DELIVERY_STATUSES.findIndex(s => s.value === status)
  }

  const stats = {
    total: orders.length,
    completed: orders.filter(o => o.status === 'delivered' || o.status === 'completed').length,
    pending: orders.filter(o => o.status === 'pending' || o.status === 'shipped' || o.status === 'processing').length,
    totalSpent: activeTab === 'purchases' 
      ? orders.filter(o => o.status !== 'cancelled').reduce((sum, o) => sum + parseFloat(o.total_amount || o.total || 0), 0)
      : 0,
    totalEarned: activeTab === 'sales'
      ? orders.filter(o => o.status !== 'cancelled').reduce((sum, o) => sum + parseFloat(o.seller_earnings || 0), 0)
      : 0
  }

  const indexOfLastOrder = currentPage * ordersPerPage
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage
  const currentOrders = orders.slice(indexOfFirstOrder, indexOfLastOrder)
  const totalPages = Math.ceil(orders.length / ordersPerPage)

  const DeliveryTracker = ({ currentStatus }) => {
    const currentIdx = getDeliveryStatusIndex(currentStatus || 'pending')
    
    return (
      <div className="py-4">
        <div className="flex items-center justify-between relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 z-0">
            <div 
              className="h-full bg-green-500 transition-all duration-500"
              style={{ width: currentIdx >= 0 ? ((currentIdx / (DELIVERY_STATUSES.length - 1)) * 100) + '%' : '0%' }}
            />
          </div>
          
          {DELIVERY_STATUSES.map((step, idx) => {
            const StepIcon = step.icon
            const isCompleted = idx <= currentIdx
            const isCurrent = idx === currentIdx
            
            return (
              <div key={step.value} className="flex flex-col items-center relative z-10">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                  isCompleted
                    ? step.bg + ' border-current ' + step.color
                    : 'bg-white border-gray-300 text-gray-400'
                } ${isCurrent ? 'ring-2 ' + step.ring + ' ring-offset-2' : ''}`}>
                  <StepIcon className="w-5 h-5" />
                </div>
                <span className={'text-xs mt-2 font-medium ' + (isCompleted ? step.color : 'text-gray-400')}>
                  {step.label}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/katale" className="text-gray-600 hover:text-gray-900 mr-4">
                <ChevronLeft className="h-5 w-5" />
              </Link>
              <Package className="h-6 w-6 text-blue-600 mr-2" />
              <h1 className="text-2xl font-bold text-gray-900">My Orders</h1>
            </div>
            <button onClick={fetchOrders} className="btn btn-secondary btn-sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
            <button onClick={fetchOrders} className="ml-auto text-red-700 hover:text-red-900 underline">
              Retry
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => { setActiveTab('purchases'); setCurrentPage(1); setExpandedOrder(null) }}
                className={'flex-1 py-4 px-4 text-center border-b-2 font-medium ' + (
                  activeTab === 'purchases'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                <ShoppingBag className="h-5 w-5 inline-block mr-2" />
                My Purchases
              </button>
              <button
                onClick={() => { setActiveTab('sales'); setCurrentPage(1); setExpandedOrder(null) }}
                className={'flex-1 py-4 px-4 text-center border-b-2 font-medium ' + (
                  activeTab === 'sales'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                <Package className="h-5 w-5 inline-block mr-2" />
                My Sales
              </button>
            </nav>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-500">Total Orders</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-500">Completed</p>
            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-500">{activeTab === 'purchases' ? 'Total Spent' : 'Total Earned'}</p>
            <p className="text-2xl font-bold text-blue-600">
              {formatCurrency(activeTab === 'purchases' ? stats.totalSpent : stats.totalEarned)}
            </p>
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {activeTab === 'purchases' ? 'purchases' : 'sales'} yet
            </h3>
            <p className="text-gray-500 mb-4">
              {activeTab === 'purchases' 
                ? 'Start shopping on MG Katale to see your orders here.' 
                : 'When you sell products, your orders will appear here.'}
            </p>
            <Link to="/katale/products" className="btn btn-primary">
              Browse Products
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {currentOrders.map((order) => {
              const statusConfig = getStatusConfig(order.status)
              const StatusIcon = statusConfig.icon
              const isExpanded = expandedOrder === order.id
              const deliveryStatus = order.delivery_status || 'pending'

              return (
                <div key={order.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center space-x-4">
                      <span className="font-medium text-gray-900">{order.order_number || ('#' + order.id)}</span>
                      <span className="text-sm text-gray-500">{formatDate(order.created_at || order.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ' + statusConfig.color}>
                        <StatusIcon className="h-4 w-4 mr-1" />
                        {statusConfig.label}
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="space-y-2 mb-4">
                      {(order.items || []).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            {item.quantity} {item.unit || 'pc'} x {item.product_name || item.name}
                          </span>
                          <span className="text-gray-900">{formatCurrency(item.item_subtotal || (item.quantity * (item.product_price || item.price)))}</span>
                        </div>
                      ))}
                    </div>

                    <div className="border-t pt-4 flex flex-wrap items-center justify-between gap-4">
                      <div>
                        {activeTab === 'purchases' ? (
                          <>
                            <p className="text-sm text-gray-500">Seller</p>
                            <p className="font-medium text-gray-900">
                              {order.items && order.items[0] ? order.items[0].seller_mg_code : 'Seller'}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-gray-500">Buyer</p>
                            <p className="font-medium text-gray-900">
                              {order.buyer_mg_code || 'Buyer'}
                            </p>
                          </>
                        )}
                      </div>
                      
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Total</p>
                        <p className="text-xl font-bold text-gray-900">{formatCurrency(order.total_amount || order.total)}</p>
                        {activeTab === 'sales' && order.seller_earnings && (
                          <p className="text-sm text-green-600 font-medium">Earnings: {formatCurrency(order.seller_earnings)}</p>
                        )}
                      </div>

                      <button 
                        onClick={() => toggleDetails(order.id)}
                        className={'btn ' + (isExpanded ? 'btn-primary' : 'btn-secondary')}
                      >
                        {isExpanded ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                        {isExpanded ? 'Hide Details' : 'View Details'}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t bg-gray-50">
                      <div className="px-4 py-4 border-b bg-white">
                        <h4 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                          <Truck className="h-5 w-5 text-blue-600" />
                          Delivery Status
                        </h4>
                        {order.delivery_updated_at && (
                          <p className="text-xs text-gray-500 mb-2">
                            Last updated: {formatDateTime(order.delivery_updated_at)}
                          </p>
                        )}
                        <DeliveryTracker currentStatus={deliveryStatus} />
                        
                        {order.delivery_notes && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                            <p className="text-sm text-blue-800 flex items-start gap-2">
                              <MessageSquare className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <span><strong>Seller note:</strong> {order.delivery_notes}</span>
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="px-4 py-4 border-b bg-white">
                        <h4 className="font-semibold text-gray-900 mb-3">Order Breakdown</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Subtotal</span>
                            <span>{formatCurrency(order.subtotal)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Delivery Fee</span>
                            <span>{formatCurrency(order.delivery_fee)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">UFCC Commission (5%)</span>
                            <span>{formatCurrency(order.ufcc_commission)}</span>
                          </div>
                          <div className="flex justify-between font-bold border-t pt-2">
                            <span>Total</span>
                            <span>{formatCurrency(order.total_amount)}</span>
                          </div>
                          {activeTab === 'sales' && order.seller_earnings && (
                            <div className="flex justify-between text-green-700 font-medium border-t pt-2">
                              <span>Your Earnings (items + delivery)</span>
                              <span>{formatCurrency(order.seller_earnings)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {order.delivery_address && (
                        <div className="px-4 py-3 border-b bg-white">
                          <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">Delivery Address</p>
                              <p className="text-sm text-gray-600">{order.delivery_address}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="px-4 py-3 border-b bg-white">
                        <div className="flex items-center gap-2">
                          {order.buyer_savings_debited == 1 ? (
                            <>
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="text-sm text-green-700 font-medium">Payment confirmed (Ref: {order.buyer_debit_ref || 'N/A'})</span>
                            </>
                          ) : (
                            <>
                              <Clock className="h-4 w-4 text-yellow-600" />
                              <span className="text-sm text-yellow-700 font-medium">Payment pending</span>
                            </>
                          )}
                        </div>
                      </div>

                      {activeTab === 'sales' && deliveryStatus !== 'delivered' && (
                        <div className="px-4 py-4 bg-blue-50 border-t border-blue-100">
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Send className="h-4 w-4 text-blue-600" />
                            Update Delivery Status
                          </h4>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">New Status</label>
                              <select
                                value={deliveryForm.status}
                                onChange={(e) => setDeliveryForm({ ...deliveryForm, status: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                              >
                                {DELIVERY_STATUSES.map((s) => {
                                  const currentIdx = getDeliveryStatusIndex(deliveryStatus)
                                  const optIdx = getDeliveryStatusIndex(s.value)
                                  if (optIdx < currentIdx) return null
                                  return (
                                    <option key={s.value} value={s.value}>
                                      {s.label}
                                    </option>
                                  )
                                })}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Note to Buyer (optional)</label>
                              <textarea
                                value={deliveryForm.notes}
                                onChange={(e) => setDeliveryForm({ ...deliveryForm, notes: e.target.value })}
                                placeholder="e.g. Your order has been dispatched via boda rider, expected by 3pm..."
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <button
                              onClick={() => handleUpdateDeliveryStatus(order.id)}
                              disabled={updatingDelivery === order.id || deliveryForm.status === deliveryStatus}
                              className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                            >
                              {updatingDelivery === order.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Updating...
                                </>
                              ) : (
                                <>
                                  <Send className="h-4 w-4" />
                                  Update Delivery Status
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}

                      {activeTab === 'sales' && deliveryStatus === 'delivered' && (
                        <div className="px-4 py-4 bg-green-50 border-t border-green-100">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <span className="text-green-700 font-medium">This order has been delivered</span>
                          </div>
                        </div>
                      )}

                      {activeTab === 'purchases' && deliveryStatus !== 'delivered' && (
                        <div className="px-4 py-3 bg-yellow-50 border-t border-yellow-100">
                          <p className="text-sm text-yellow-800 flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            The seller will update the delivery status as your order progresses.
                          </p>
                        </div>
                      )}

                      {activeTab === 'purchases' && deliveryStatus === 'delivered' && (
                        <div className="px-4 py-4 bg-green-50 border-t border-green-100">
                          {order.escrow_status === 'released' || order.buyer_confirmed_receipt ? (
                            <div className="flex items-center gap-2">
                              <ShieldCheck className="h-5 w-5 text-green-600" />
                              <span className="text-green-700 font-medium">Delivery confirmed & payment released to seller</span>
                            </div>
                          ) : order.escrow_status === 'disputed' ? (
                            <div className="flex items-center gap-2">
                              <ShieldAlert className="h-5 w-5 text-red-600" />
                              <span className="text-red-700 font-medium">Dispute in progress — UFCC is reviewing</span>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Shield className="h-5 w-5 text-blue-600" />
                                <span className="text-blue-800 font-semibold">Buyer Protection — Confirm Delivery</span>
                              </div>
                              <p className="text-sm text-gray-600">
                                Your payment is held in escrow. Please confirm you received the goods in acceptable condition to release payment to the seller.
                              </p>
                              <div className="flex flex-wrap gap-3">
                                <button
                                  onClick={() => handleConfirmDeliveryReceipt(order.id, 'accepted')}
                                  disabled={confirmingReceipt === order.id}
                                  className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium text-sm"
                                >
                                  {confirmingReceipt === order.id ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                  ) : (
                                    <ThumbsUp className="w-4 h-4" />
                                  )}
                                  Accept & Release Payment
                                </button>
                                <button
                                  onClick={() => { setDisputeOrder(order); setShowDisputeModal(true) }}
                                  className="flex items-center gap-2 px-5 py-2.5 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 font-medium text-sm"
                                >
                                  <AlertTriangle className="w-4 h-4" />
                                  Raise Dispute
                                </button>
                              </div>
                              <p className="text-xs text-gray-400 mt-1">
                                If poor quality products are delivered and refused, the seller bears the cost of return transport.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}

            {totalPages > 1 && (
              <div className="flex items-center justify-between bg-white rounded-lg shadow-sm px-4 py-3">
                <p className="text-sm text-gray-500">
                  Showing {indexOfFirstOrder + 1} to {Math.min(indexOfLastOrder, orders.length)} of {orders.length} orders
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => prev - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="text-sm font-medium">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Buyer Protection Escrow Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mt-4">
          <div className="flex items-start gap-3">
            <Shield className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Buyer Protection Escrow</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• When you place an order, your payment is held in escrow (not released to the seller immediately).</li>
                <li>• Once the seller marks the order as "Delivered", you have the option to confirm receipt or raise a dispute.</li>
                <li>• Upon your confirmation, the escrowed funds are released to the seller.</li>
                <li>• If you raise a dispute, UFCC (as clearing house) will review and mediate.</li>
                <li>• <strong>If poor quality products are delivered and refused by the buyer, the seller bears the burden of transporting the goods back.</strong></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Dispute Modal */}
      {showDisputeModal && disputeOrder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <ShieldAlert className="h-6 w-6 text-red-600" />
                <h3 className="text-lg font-bold text-gray-900">Raise a Dispute</h3>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
                <p className="font-medium">Order: {disputeOrder.order_number || `#${disputeOrder.id}`}</p>
                <p className="text-xs text-red-500 mt-1">
                  UFCC will review your dispute. If the product is of poor quality and refused, the seller bears the cost of return shipping.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Reason for Dispute *</label>
                  <select
                    value={disputeForm.reason}
                    onChange={(e) => setDisputeForm({ ...disputeForm, reason: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                  >
                    <option value="">Select a reason...</option>
                    <option value="poor_quality">Poor quality product</option>
                    <option value="wrong_item">Wrong item delivered</option>
                    <option value="damaged">Product damaged during delivery</option>
                    <option value="incomplete_order">Incomplete order / missing items</option>
                    <option value="not_as_described">Product not as described</option>
                    <option value="quantity_mismatch">Quantity does not match order</option>
                    <option value="never_received">Never received the order</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Describe the Issue *</label>
                  <textarea
                    value={disputeForm.description}
                    onChange={(e) => setDisputeForm({ ...disputeForm, description: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows={3}
                    placeholder="Please describe the problem in detail..."
                    maxLength={1000}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => { setShowDisputeModal(false); setDisputeOrder(null); setDisputeForm({ reason: '', description: '' }) }}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleRaiseDispute}
                disabled={submittingDispute || !disputeForm.reason || !disputeForm.description.trim()}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center gap-2"
              >
                {submittingDispute ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <AlertTriangle className="w-4 h-4" />
                )}
                Submit Dispute
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default KataleOrders
